import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    makeStyles,
    Typography,
    Paper,
    Grid,
    Box,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { KeyboardDateTimePicker } from "@material-ui/pickers";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SaveIcon from "@material-ui/icons/Save";
import apiHelper from "../../../util/ApiHelper";
import { useLoading } from "../../../util/LoadingProvider";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import ImageUploadField from "../../common/ImageUploadField";

const useStyles = makeStyles((theme) => ({
    content: {
        padding: theme.spacing(3),
    },
    header: {
        marginBottom: theme.spacing(3),
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    backButton: {
        marginRight: theme.spacing(2),
    },
    formCard: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    formField: {
        marginBottom: theme.spacing(2),
    },
    buttonGroup: {
        display: "flex",
        gap: theme.spacing(2),
        marginTop: theme.spacing(3),
    },
    imagePreview: {
        maxWidth: "200px",
        maxHeight: "150px",
        marginTop: theme.spacing(1),
        border: "1px solid #cccccc",
        borderRadius: "4px",
        padding: theme.spacing(1),
    },
    imageContainer: {
        marginTop: theme.spacing(1),
    },
    imageLink: {
        color: "#306F9D",
        textDecoration: "none",
        fontSize: "12px",
        marginLeft: theme.spacing(1),
    },
    input: {
        width: "100%",
        "& button": {
            fontSize: "13px",
            lineHeight: "15px",
            width: "25%",
        },
    },
    sectionText: {
        fontSize: "13px",
        fontWeight: 700,
        paddingLeft: "4px",
    },
    fileInput: {
        width: "100%",
        marginBottom: "12px",
        "& .MuiInputLabel-outlined": {
            transform: "translate(14px, 13px)",
        },
        "& .MuiOutlinedInput-adornedEnd": {
            height: "40px",
            paddingRight: "5px",
        },
        "& input": {
            fontSize: "13px",
        },
    },
}));

const BannerEditingPage = () => {
    const classes = useStyles();
    const history = useHistory();
    const { id } = useParams();
    const { setLoading } = useLoading();
    
    const isEditMode = !!id;
    
    const [formData, setFormData] = useState({
        type: "middle",
        imageEn: "",
        imageTc: "",
        image2En: "",
        image2Tc: "",
        urlEn: "",
        urlTc: "",
        startTime: null,
        endTime: null,
        status: 1, // 1 = Active, 0 = Inactive
        memberOnly: false,
    });

    const [errors, setErrors] = useState({});

    // Load existing banner data if in edit mode
    useEffect(() => {
        if (isEditMode) {
            loadBannerData();
        }
    }, [id]);

    const loadBannerData = async () => {
        setLoading(true);
        try {
            const response = await apiHelper.cineclub.getHomepageBannerById(id);
            if (response.data) {
                const banner = response.data;
                // Convert status string to number: "ACTIVE" -> 1, "INACTIVE" -> 0
                let statusValue = 1;
                if (banner.status === "INACTIVE" || banner.status === 0) {
                    statusValue = 0;
                }
                
                setFormData({
                    type: banner.type || "middle",
                    // Store the image value as-is (could be URL or filename)
                    imageEn: banner.imageEn || "",
                    imageTc: banner.imageTc || "",
                    image2En: banner.image2En || "",
                    image2Tc: banner.image2Tc || "",
                    urlEn: banner.urlEn || "",
                    urlTc: banner.urlTc || "",
                    startTime: banner.startTime ? dayjs(banner.startTime) : null,
                    endTime: banner.endTime ? dayjs(banner.endTime) : null,
                    status: statusValue,
                    memberOnly: !!banner.memberOnly,
                });
            }
        } catch (error) {
            console.error("Error loading banner data:", error);
            alert("Failed to load banner data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field) => (event) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const handleTypeChange = (event) => {
        const value = event.target.value;
        if (value === "middle") {
            setFormData(prev => ({
                ...prev,
                type: value,
                memberOnly: false,
                image2En: "",
                image2Tc: "",
            }));
            setErrors(prev => {
                const { image2En, image2Tc, ...rest } = prev;
                return rest;
            });
        } else {
            setFormData(prev => ({
                ...prev,
                type: value,
            }));
        }
    };


    const handleMemberOnlyChange = (event) => {
        setFormData(prev => ({
            ...prev,
            memberOnly: event.target.checked
        }));
    };

    const handleDateTimeChange = (field) => (value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user changes date
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.imageEn || !formData.imageEn.trim()) {
            newErrors.imageEn = "English image is required";
        }
        
        if (!formData.imageTc || !formData.imageTc.trim()) {
            newErrors.imageTc = "Traditional Chinese image is required";
        }

        if (formData.type === "crazy_ad") {
            if (!formData.image2En || !formData.image2En.trim()) {
                newErrors.image2En = "English image (mobile) is required";
            }
            if (!formData.image2Tc || !formData.image2Tc.trim()) {
                newErrors.image2Tc = "Traditional Chinese image (mobile) is required";
            }
        }
        
        if (!formData.startTime || (typeof formData.startTime === 'object' && !dayjs(formData.startTime).isValid())) {
            newErrors.startTime = "Start time is required";
        }
        
        if (!formData.endTime || (typeof formData.endTime === 'object' && !dayjs(formData.endTime).isValid())) {
            newErrors.endTime = "End time is required";
        }
        
        // Validate date range
        if (formData.startTime && formData.endTime && dayjs(formData.startTime).isValid() && dayjs(formData.endTime).isValid()) {
            const startTime = dayjs(formData.startTime);
            const endTime = dayjs(formData.endTime);
            if (startTime.isAfter(endTime) || startTime.isSame(endTime)) {
                newErrors.endTime = "End time must be after start time";
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            // Extract filename from image URLs (remove path, keep only filename)
            const extractFilename = (url) => {
                if (!url || !url.trim()) return null;
                const trimmed = url.trim();
                // If it's already just a filename (no slashes), return as is
                if (!trimmed.includes("/")) return trimmed;
                // Extract filename from URL
                return trimmed.split("/").pop();
            };

            const payload = {
                type: (formData.type && formData.type.trim()) || "middle",
                imageEn: extractFilename(formData.imageEn) || "",
                imageTc: extractFilename(formData.imageTc) || "",
                image2En: extractFilename(formData.image2En),
                image2Tc: extractFilename(formData.image2Tc),
                urlEn: formData.urlEn.trim() || null,
                urlTc: formData.urlTc.trim() || null,
                startTime: formData.startTime ? dayjs(formData.startTime).format("YYYY-MM-DDTHH:mm:00") : null,
                endTime: formData.endTime ? dayjs(formData.endTime).format("YYYY-MM-DDTHH:mm:00") : null,
                status: formData.status === 1 ? "ACTIVE" : "INACTIVE",
                memberOnly: !!formData.memberOnly,
            };

            let response;
            if (isEditMode) {
                response = await apiHelper.cineclub.updateHomepageBanner(id, payload);
            } else {
                response = await apiHelper.cineclub.createHomepageBanner(payload);
            }
            
            // Log the response to debug
            console.log("API Response:", response);
            
            if (response.data && response.data.code === 0) {
                // Success - show toast and redirect back to listing page
                const action = isEditMode ? "updated" : "created";
                toast.dark(`Banner ${action} successfully.`);
                history.push("/cineclub/membership/banners");
            } else {
                // Handle error response
                const action = isEditMode ? "update" : "create";
                console.error(`Failed to ${action} banner:`, response);
                alert(`Failed to ${action} banner. Please try again.`);
            }
        } catch (error) {
            const action = isEditMode ? "updating" : "creating";
            console.error(`Error ${action} banner:`, error);
            alert(`An error occurred while ${action} the banner. Please try again.`);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        history.push("/cineclub/membership/banners");
    };

    const handleFileUpload = async (field, file) => {
        if (!file) return;

        try {
            setLoading(true);
            const result = await apiHelper.cineclub.uploadMembershipFile(file, "HOMEPAGE");
            const url = result.data.url;
            // Store the full URL for display, we'll extract filename when submitting
            setFormData(prev => ({
                ...prev,
                [field]: url
            }));
        } catch (error) {
            console.error("Error uploading file:", error);
            alert("Failed to upload image. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleImageDelete = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: ""
        }));
    };

    return (
        <div className={classes.content}>
            <Box className={classes.header}>
                <Button
                    className={classes.backButton}
                    startIcon={<ArrowBackIcon />}
                    onClick={handleCancel}
                >
                    Back to Banners
                </Button>
                <Typography variant="h4" component="h1">
                    {isEditMode ? "Edit Homepage Banner" : "Create Homepage Banner"}
                </Typography>
            </Box>

            <Paper className={classes.formCard}>
                <Typography variant="h6" gutterBottom>
                    Banner Details
                </Typography>
                
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <FormControl
                            fullWidth
                            variant="outlined"
                            className={classes.formField}
                        >
                            <InputLabel id="banner-type-label">Type</InputLabel>
                            <Select
                                labelId="banner-type-label"
                                value={formData.type || "middle"}
                                onChange={handleTypeChange}
                                label="Type"
                            >
                                <MenuItem value="middle">Middle</MenuItem>
                                <MenuItem value="crazy_ad">Crazy Ad</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box className={classes.formField}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!formData.memberOnly}
                                        onChange={handleMemberOnlyChange}
                                        color="primary"
                                        disabled={formData.type === "middle"}
                                        inputProps={{ "aria-label": "Member only" }}
                                    />
                                }
                                label="Paid Member only"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ImageUploadField
                            field="imageEn"
                            value={formData.imageEn}
                            onUpload={handleFileUpload}
                            onDelete={handleImageDelete}
                            label="English Image *"
                            required={true}
                            error={errors.imageEn}
                            classes={classes}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ImageUploadField
                            field="imageTc"
                            value={formData.imageTc}
                            onUpload={handleFileUpload}
                            onDelete={handleImageDelete}
                            label="Traditional Chinese Image *"
                            required={true}
                            error={errors.imageTc}
                            classes={classes}
                        />
                    </Grid>

                    {formData.type === "crazy_ad" && (
                        <>
                            <Grid item xs={12} md={6}>
                                <ImageUploadField
                                    field="image2En"
                                    value={formData.image2En}
                                    onUpload={handleFileUpload}
                                    onDelete={handleImageDelete}
                                    label="English Image (mobile) *"
                                    required={true}
                                    error={errors.image2En}
                                    classes={classes}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ImageUploadField
                                    field="image2Tc"
                                    value={formData.image2Tc}
                                    onUpload={handleFileUpload}
                                    onDelete={handleImageDelete}
                                    label="Traditional Chinese Image (mobile) *"
                                    required={true}
                                    error={errors.image2Tc}
                                    classes={classes}
                                />
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="English Link URL (Optional)"
                            variant="outlined"
                            className={classes.formField}
                            value={formData.urlEn}
                            onChange={handleInputChange("urlEn")}
                            helperText="Optional link URL for English banner"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Traditional Chinese Link URL (Optional)"
                            variant="outlined"
                            className={classes.formField}
                            value={formData.urlTc}
                            onChange={handleInputChange("urlTc")}
                            helperText="Optional link URL for Traditional Chinese banner"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <KeyboardDateTimePicker
                            fullWidth
                            variant="dialog"
                            format="d MMM yyyy, hh:mma"
                            margin="dense"
                            id="start-datetime-picker"
                            label="Start Date"
                            value={formData.startTime}
                            onChange={handleDateTimeChange("startTime")}
                            className={classes.formField}
                            InputLabelProps={{
                                shrink: true,
                                style: {
                                    fontWeight: 700,
                                    color: "#333333",
                                },
                            }}
                            KeyboardButtonProps={{
                                "aria-label": "change start date and time",
                            }}
                            error={!!errors.startTime}
                            helperText={errors.startTime}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <KeyboardDateTimePicker
                            fullWidth
                            variant="dialog"
                            format="d MMM yyyy, hh:mma"
                            margin="dense"
                            id="end-datetime-picker"
                            label="End Date"
                            value={formData.endTime}
                            onChange={handleDateTimeChange("endTime")}
                            className={classes.formField}
                            InputLabelProps={{
                                shrink: true,
                                style: {
                                    fontWeight: 700,
                                    color: "#333333",
                                },
                            }}
                            KeyboardButtonProps={{
                                "aria-label": "change end date and time",
                            }}
                            error={!!errors.endTime}
                            helperText={errors.endTime}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box className={classes.formField}>
                            <Typography variant="body2" className={classes.sectionText} style={{ marginBottom: "8px" }}>
                                Status
                            </Typography>
                            <ToggleButtonGroup
                                value={formData.status}
                                onChange={() => {}}
                                exclusive
                                aria-label="Status"
                                className={classes.input}
                                style={{ pointerEvents: "none" }}
                            >
                                <ToggleButton
                                    value={1}
                                    aria-label="Active"
                                    style={{ width: "50%" }}
                                >
                                    Active
                                </ToggleButton>
                                <ToggleButton
                                    value={0}
                                    aria-label="Inactive"
                                    style={{ width: "50%" }}
                                >
                                    Inactive
                                </ToggleButton>
                            </ToggleButtonGroup>
                            {errors.status && (
                                <Typography variant="caption" color="error" style={{ marginTop: "8px", display: "block" }}>
                                    {errors.status}
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                </Grid>

                <Box className={classes.buttonGroup}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSubmit}
                    >
                        {isEditMode ? "Update" : "Create"}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                </Box>
            </Paper>
        </div>
    );
};

export default BannerEditingPage;

